import { ResourceName, Task, Claimant, SendEligibilitySignatureRequest, AlertType, ObjectType, User } from "@/types";
import { Box, Typography } from "@mui/material";
import { useGetIdentity, useGetOne, useNotify, useRefresh } from "react-admin";
import { useCallback, useEffect, useState } from "react";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { DEFAULT_PANDADOC_FORM_DATA } from "./PandaDocTemplateRegistry";
import { ClaimantVerificationForm } from "./ClaimantVerificationForm";
import dataProvider from "@/providers/dataProvider/dataProvider";

interface PandaDocExpansionProps {
  task: Task;
  taskTemplate?: { title: string };
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const PandaDocExpansion = ({ task, taskTemplate, onSuccess, onCancel }: PandaDocExpansionProps) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const { data: currentUser } = useGetIdentity();
  const [step, setStep] = useState<1 | 2>(1);
  const [formData, setFormData] = useState<SendEligibilitySignatureRequest>(DEFAULT_PANDADOC_FORM_DATA);
  const [isSending, setIsSending] = useState(false);

  // Load claimant data if needed
  const { data: claimantData, isLoading: isLoadingClaimant } = useGetOne<Claimant>(
    ResourceName.CLAIMANTS,
    { id: task.record?.object_id || undefined },
    {
      enabled: task.record?.object_type === ObjectType.CLAIMANTS && !!task.record?.object_id,
    }
  );

  const { data: currentUserData, isLoading: isLoadingCurrentUserData } = useGetOne<User>(
    ResourceName.USERS,
    { id: (currentUser?.id as string) || undefined },
    {
      enabled: task.record?.object_type === ObjectType.CLAIMANTS && !!task.record?.object_id,
      retry: false,
    }
  );

  // Only treat as loading on initial fetch — background re-fetches from react-admin's
  // global refresh() should not flicker the Continue button disabled state.
  const isLoading = (isLoadingClaimant && !claimantData) || (isLoadingCurrentUserData && !currentUserData);

  useEffect(() => {
    if (currentUserData?.email) {
      setFormData((prev) => ({ ...prev, reply_to: { email: currentUserData.email } }));
    }
  }, [currentUserData?.email]);

  const handleFormChange = (updates: Partial<SendEligibilitySignatureRequest>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleProceedToStep2 = () => {
    setStep(2);
  };

  const handleSend = useCallback(async () => {
    setIsSending(true);
    try {
      await dataProvider.invoke(`tasks/${task.id}/signatures`, {
        data: formData,
      });
      notify("Document sent successfully", { type: AlertType.SUCCESS });
      refresh();
      onSuccess?.();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsSending(false);
      setStep(1);
      setFormData(DEFAULT_PANDADOC_FORM_DATA);
    }
  }, [task, formData, notify, notifyError, refresh, onSuccess]);

  const handleCancel = () => {
    setStep(1);
    setFormData(DEFAULT_PANDADOC_FORM_DATA);
    onCancel?.();
  };

  if (!taskTemplate) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No template found for this task.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        mt: 1,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <ClaimantVerificationForm
        task={task}
        template={taskTemplate}
        claimantData={claimantData}
        isLoading={isLoading}
        step={step}
        formData={formData}
        onFormChange={handleFormChange}
        onProceedToStep2={handleProceedToStep2}
        onSend={handleSend}
        onCancel={handleCancel}
        isSending={isSending}
      />
    </Box>
  );
};
